import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ImageOff, RefreshCw, Cloud } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from "../config/api";
import { isCloudinaryUrl, getContextualImageUrl, FALLBACK_PLACEHOLDER } from "../utils/cloudinary";

// Fallback placeholder image
const PLACEHOLDER_IMAGE = FALLBACK_PLACEHOLDER;

// Helper to normalize image URLs - handles different formats
const getImageUrl = (url) => {
  if (!url) return "";
  
  // Already a full external URL - check various cases
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // Trusted external CDNs - return as-is
    const trustedDomains = [
      'unsplash.com',
      'images.unsplash',
      'pexels.com',
      'cloudinary.com',
      'imgur.com',
      'customer-assets.emergentagent.com'
    ];
    
    for (const domain of trustedDomains) {
      if (url.includes(domain)) {
        return url;
      }
    }
    
    // Old preview domain URLs that need fixing
    if (url.includes(".preview.emergentagent.com/api/uploads/") || 
        url.includes(".preview.emergentagent.com/uploads/")) {
      // Extract just the filename and use same-origin path
      const parts = url.split("/uploads/");
      if (parts.length > 1) {
        const filename = parts[parts.length - 1].split("?")[0]; // Remove query params
        return `/api/uploads/${decodeURIComponent(filename)}`;
      }
    }
    
    // Other external URLs - keep as-is
    return url;
  }
  
  // Already normalized with /api/uploads/
  if (url.startsWith("/api/uploads/")) return url;
  
  // Old format /uploads/
  if (url.startsWith("/uploads/")) {
    return "/api" + url;
  }
  
  // Relative path uploads/...
  if (url.startsWith("uploads/")) {
    return "/api/" + url;
  }
  
  // Already a root-relative path
  if (url.startsWith("/")) return url;
  
  // Just a filename - assume it's in uploads
  return `/api/uploads/${url}`;
};

// Resilient gallery image card with error handling
function GalleryImageCard({ image, idx, onClick }) {
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Apply Cloudinary optimization if it's a Cloudinary URL
  const rawUrl = getImageUrl(image.url);
  const optimizedUrl = isCloudinaryUrl(rawUrl) 
    ? getContextualImageUrl(rawUrl, idx % 7 === 0 ? 'full' : 'gallery')
    : rawUrl;
  
  const [imgSrc, setImgSrc] = useState(optimizedUrl);

  const handleError = () => {
    // Try fallback to placeholder
    if (imgSrc !== PLACEHOLDER_IMAGE) {
      setImgSrc(PLACEHOLDER_IMAGE);
    } else {
      setHasError(true);
    }
  };

  const handleRetry = (e) => {
    e.stopPropagation();
    setIsRetrying(true);
    setImgSrc(optimizedUrl + `?retry=${Date.now()}`);
    setHasError(false);
    setTimeout(() => setIsRetrying(false), 1000);
  };

  if (hasError) {
    return (
      <div
        className={`relative overflow-hidden rounded-sm cursor-pointer group bg-gray-100 flex flex-col items-center justify-center aspect-square ${
          idx % 7 === 0 ? "md:col-span-2 md:row-span-2" : ""
        }`}
        onClick={onClick}
        data-testid={`gallery-image-${idx}`}
      >
        <ImageOff className="w-12 h-12 text-gray-400 mb-2" />
        <p className="text-gray-500 text-sm mb-2">Image unavailable</p>
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center gap-1 text-sm text-kerala-green hover:text-kerala-gold transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
          {isRetrying ? 'Retrying...' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-sm cursor-pointer group ${
        idx % 7 === 0 ? "md:col-span-2 md:row-span-2" : ""
      }`}
      onClick={onClick}
      data-testid={`gallery-image-${idx}`}
    >
      {/* Cloudinary badge */}
      {isCloudinaryUrl(image.url) && (
        <div className="absolute top-2 right-2 z-10 bg-green-500/80 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
          <Cloud className="w-3 h-3" />
        </div>
      )}
      <img
        src={imgSrc}
        alt={image.title}
        className="w-full h-full object-cover aspect-square group-hover:scale-110 transition-transform duration-500"
        loading="lazy"
        onError={handleError}
      />
      <div className="absolute inset-0 bg-kerala-green/0 group-hover:bg-kerala-green/40 transition-colors duration-300 flex items-end">
        <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p className="text-white font-medium">{image.title}</p>
          <p className="text-white/70 text-sm">{image.event_name} • {image.year}</p>
        </div>
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const [imagesRes, yearsRes] = await Promise.all([
        fetch(`${API}/gallery`),
        fetch(`${API}/gallery/years`)
      ]);

      if (imagesRes.ok) setImages(await imagesRes.json());
      if (yearsRes.ok) setYears(await yearsRes.json());
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = selectedYear === "all" 
    ? images 
    : images.filter(img => img.year === parseInt(selectedYear));

  const openLightbox = (idx) => {
    setCurrentImageIdx(idx);
    setLightboxOpen(true);
  };

  const navigateLightbox = (delta) => {
    setCurrentImageIdx(prev => {
      const newIdx = prev + delta;
      if (newIdx < 0) return filteredImages.length - 1;
      if (newIdx >= filteredImages.length) return 0;
      return newIdx;
    });
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") navigateLightbox(-1);
      if (e.key === "ArrowRight") navigateLightbox(1);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  return (
    <div className="pt-20 bg-kerala-cream min-h-screen" data-testid="gallery-page">
      {/* Header */}
      <section className="py-20 bg-kerala-green">
        <div className="section-container text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">Memories</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white mb-4">
            Photo Gallery
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Relive the beautiful moments from our past celebrations and community events.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-kerala-gold/10">
        <div className="section-container flex justify-between items-center">
          <p className="text-gray-600">
            {filteredImages.length} photo{filteredImages.length !== 1 ? 's' : ''}
          </p>
          
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-40" data-testid="year-filter">
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="section-container">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600">No photos available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, idx) => (
                <GalleryImageCard 
                  key={image.id}
                  image={image}
                  idx={idx}
                  onClick={() => openLightbox(idx)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && filteredImages[currentImageIdx] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="lightbox">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/10"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-6 h-6" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
            onClick={() => navigateLightbox(-1)}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
            onClick={() => navigateLightbox(1)}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>

          <div className="max-w-5xl max-h-[80vh] px-4">
            <img
              src={getImageUrl(filteredImages[currentImageIdx].url)}
              alt={filteredImages[currentImageIdx].title}
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
            <div className="text-center mt-4">
              <p className="text-white font-medium">{filteredImages[currentImageIdx].title}</p>
              <p className="text-white/60 text-sm">
                {filteredImages[currentImageIdx].event_name} • {filteredImages[currentImageIdx].year}
              </p>
              <p className="text-white/40 text-sm mt-2">
                {currentImageIdx + 1} / {filteredImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
